import {connect} from 'react-redux';

import {defineMessage} from '../../../../../../libs/shared/core/src/helpers/internationalization/message';
import {buildTeammateDisplayName} from '../../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {BasicActivityModel} from '../../../../../models/api/activityModel';
import {
  ActivitySourceTypesEnum,
  ActivitySourceVisualTypesEnum,
} from '../../../../../models/api/activitySourceModel';
import {AppState} from '../../../../../store/appState';
import {ThunkAction} from '../../../../../store/appThunk';
import {makeGetChannel} from '../../../../../store/data/channels/channelsSelectors';
import {getFindTeammate, getUserId} from '../../../../../store/data/teammates/teammatesSelectors';
import {mergeConversationsAndNavigateAsyncAction} from '../../../../../store/panels/viewer/viewerPanelConversationsActions';
import {getLocalize} from '../../../../../store/stateless/internationalizationSelectors';
import {navigateToConversation} from '../../../../../store/stateless/navigationActions';
import {SplitConversationBanner} from './splitConversationBanner';

/*
 * Props.
 */

interface SplitConversationBannerContainerProps {
  activity: BasicActivityModel;
  conversationChannelIds: ReadonlyArray<number>;
}

/*
 * Selectors.
 */

function getBasicActivity(_state: AppState, props: SplitConversationBannerContainerProps) {
  return props.activity;
}

function getFirstChannelId(_state: AppState, props: SplitConversationBannerContainerProps) {
  return props.conversationChannelIds[0];
}

/*
 * Redux.
 */

function makeGetSourceTeammate() {
  return createSelector([getBasicActivity, getFindTeammate], (activity, findTeammate) => {
    const {source} = activity;
    if (source.visualType !== ActivitySourceVisualTypesEnum.TEAMMATE) {
      return undefined;
    }

    return findTeammate(source.teammateId);
  });
}

function makeGetSourceTeammateName(getSourceTeammate: ReturnType<typeof makeGetSourceTeammate>) {
  return createSelector([getLocalize, getUserId, getSourceTeammate], (localize, userId, sourceTeammate) => {
    if (!sourceTeammate) {
      return '';
    }

    if (sourceTeammate.id === userId) {
      return localize(
        defineMessage({
          id: 'activity-banner-split-body-you',
          message: 'you',
        }),
      );
    }

    return buildTeammateDisplayName(sourceTeammate);
  });
}

function makeGetIsAutoSplit() {
  return createSelector(
    [getBasicActivity],
    (activity) => activity.source.type === ActivitySourceTypesEnum.INACTIVE,
  );
}

function makeGetIsLinkConversationSplit() {
  return createSelector(
    [getBasicActivity],
    (activity) => activity.source.type === ActivitySourceTypesEnum.LINK_CONVERSATION,
  );
}

const mapStateToProps = makeMapStateToProps<SplitConversationBannerContainerProps>()(() => {
  const getSourceTeammate = makeGetSourceTeammate();

  return {
    sourceTeammateName: makeGetSourceTeammateName(getSourceTeammate),
    isAutoSplit: makeGetIsAutoSplit(),
    isLinkConversationSplit: makeGetIsLinkConversationSplit(),
    firstChannel: makeGetChannel(getFirstChannelId),
  };
});

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  onSeeOriginalConversationClick:
    (conversationId: number): ThunkAction =>
    (dispatch) => {
      trackInterfaceEvent(InterfaceEventsEnum.SPLIT_CONVERSATION_BANNER_ACTION, {
        conversation_id: conversationId,
      });

      dispatch(navigateToConversation(conversationId));
    },
  onMergeConversationsClick: mergeConversationsAndNavigateAsyncAction,
}));

export const SplitConversationBannerContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(SplitConversationBanner);
